import {
  $create,
  $getState,
  $isRootOrShadowRoot,
  $setState,
  createState,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementNode,
  LexicalNode,
} from 'lexical';
import { $unwrapNode } from '@lexical/utils';
import { Source } from '../types';
import { isValidSource } from '../config/transformers/suggestion';
import { SUGGESTION_COLORS } from './suggestion-colors';
import { $isEffectivelyEmpty } from './suggestion-utils';

type SuggestionState = {
  source: Source;
  tooltip: string | null;
  focusedAt: number | null;
  isFullCell: boolean;
  isBlock: boolean;
  markersHidden: boolean;
};

const suggestionState = createState<'suggestion-state', SuggestionState>('suggestion-state', {
  parse: () => {
    return {
      source: 'default',
      tooltip: null,
      focusedAt: null,
      isFullCell: false,
      isBlock: false,
      markersHidden: false,
    };
  },
});

export class SuggestionNode extends ElementNode {
  $config() {
    return this.config('suggestion', {
      extends: ElementNode,
      stateConfigs: [{ flat: true, stateConfig: suggestionState }],
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      suggestion: () => ({
        conversion: (element): DOMConversionOutput => {
          const sourceAttr = element.getAttribute('source');
          const source = isValidSource(sourceAttr) ? sourceAttr : 'default';
          const tooltip = element.getAttribute('tooltip') || null;
          const node = $createSuggestionNode(source, false, tooltip);
          return { node };
        },
        priority: 0,
      }),
    };
  }

  createDOM(): HTMLElement {
    const currentState = $getState(this, suggestionState);
    const container = document.createElement(currentState.isBlock ? 'div' : 'span');
    container.dataset['suggestion'] = 'true';
    container.dataset['suggestionSource'] = currentState.source;

    applySuggestionStyle(container, currentState);

    return container;
  }

  updateDOM(_prevNode: this, dom: HTMLElement): boolean {
    const currentState = $getState(this, suggestionState);
    dom.dataset['suggestionSource'] = currentState.source;
    applySuggestionStyle(dom, currentState);
    return false;
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement('suggestion');
    return { element: container };
  }
}

function applySuggestionStyle(element: HTMLElement, state: SuggestionState) {
  const defaultPadding = state.isBlock ? '8px' : '2px 4px';

  if (state.isBlock) {
    element.style.display = 'block';
    element.style.width = '100%';
    element.style.borderRadius = '8px';
    element.style.position = 'relative';
  } else {
    element.style.display = 'inline';
    element.style.borderRadius = '4px';
  }

  const bgColor =
    SUGGESTION_COLORS[state.source][state.focusedAt != null ? 'focused' : 'unfocused'];

  element.style.transition = 'background-color 0.4s ease-in-out, padding 0.4s ease-in-out';

  if (state.isFullCell) {
    element.style.padding = '0';
    element.style.backgroundColor = 'transparent';
  } else {
    element.style.padding = state.markersHidden ? '0' : defaultPadding;
    element.style.backgroundColor = state.markersHidden ? 'transparent' : bgColor;
  }

  element.style.setProperty(
    '--table-scroll-mask',
    state.markersHidden ? 'var(--mantine-color-body)' : bgColor,
  );

  element.style.textDecoration =
    state.source === 'deletion' && !state.markersHidden ? 'line-through' : 'none';
  if (state.tooltip) {
    element.dataset.tooltip = state.tooltip;
  } else {
    delete element.dataset.tooltip;
  }
}

export function $createSuggestionNode(
  source: Source = 'default',
  isBlock: boolean = false,
  tooltip: string | null = null,
): SuggestionNode {
  return $setState($create(SuggestionNode), suggestionState, {
    source,
    tooltip,
    focusedAt: null,
    isFullCell: false,
    isBlock,
    markersHidden: false,
  });
}

export function $isSuggestionNode(node: LexicalNode | null | undefined): node is SuggestionNode {
  return node instanceof SuggestionNode;
}

export function $focusSuggestion(node: SuggestionNode) {
  const currentState = $getState(node, suggestionState);
  if (currentState.focusedAt != null) return;
  $setState(node, suggestionState, { ...currentState, focusedAt: Date.now() });
}

export function $sourceSuggestion(node: SuggestionNode) {
  const value = $getState(node, suggestionState);
  return value.source;
}

export function $getSuggestionState(node: SuggestionNode) {
  return $getState(node, suggestionState);
}

export function $setSuggestionFullCell(node: SuggestionNode, isFullCell: boolean) {
  const currentState = $getState(node, suggestionState);
  $setState(node, suggestionState, { ...currentState, isFullCell });
  node.markDirty();
}

export function $setMarkersHidden(node: SuggestionNode, markersHidden: boolean) {
  const currentState = $getState(node, suggestionState);
  if (currentState.markersHidden !== markersHidden) {
    $setState(node, suggestionState, { ...currentState, markersHidden });
    node.markDirty();
  }
}

export function $dismissSuggestion(node: SuggestionNode): void {
  const parent = node.getParent();
  const grandParent = parent?.getParent();
  $unwrapNode(node);
  parent?.markDirty();
  grandParent?.markDirty();
}

export function $setSuggestionSource(node: SuggestionNode, source: Source) {
  const currentState = $getState(node, suggestionState);
  if (currentState.source !== source) {
    $setState(node, suggestionState, { ...currentState, source });
    node.markDirty();
  }
}

export function $removeSuggestionWithContent(node: SuggestionNode): void {
  let parent = node.getParent();
  node.remove();

  // Walk up and remove ancestors left empty after the deletion
  while (parent && !$isRootOrShadowRoot(parent) && $isEffectivelyEmpty(parent)) {
    const grandparent = parent.getParent();
    parent.remove();
    parent = grandparent;
  }
}
